import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '@/services/api'
import { Card, Button, Table, StatusBadge } from '@/components/ui'
import { formatListTimestamp } from '@/utils/format-date'
import type { CanaryWithStats } from '@spoofio/shared/types'

export function CanaryListPage() {
  const navigate = useNavigate()
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

  const { data: organizations } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.listOrganizations(),
  })

  // Select first org by default
  const orgId = selectedOrgId ?? organizations?.[0]?.id

  const { data: canaries, isLoading, error } = useQuery({
    queryKey: ['canaries', orgId],
    queryFn: () => (orgId ? api.listCanaries(orgId) : Promise.resolve([])),
    enabled: !!orgId,
  })

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (c: CanaryWithStats) => (
        <div>
          <Link
            to={`/canaries/${c.id}`}
            className="font-medium text-primary-600 hover:text-primary-700"
          >
            {c.name}
          </Link>
          {c.description && (
            <p className="text-sm text-gray-500 truncate max-w-xs">
              {c.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'domains',
      header: 'Authorized Domains',
      render: (c: CanaryWithStats) => (
        <span className="text-gray-600">
          {c.authorizedDomains.length} domain
          {c.authorizedDomains.length !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'detections',
      header: 'Detections (24h)',
      render: (c: CanaryWithStats) => (
        <span className={c.detectionCount24h > 0 ? 'text-warning-600 font-medium' : 'text-gray-600'}>
          {c.detectionCount24h}
        </span>
      ),
    },
    {
      key: 'alerts',
      header: 'Alerts (24h)',
      render: (c: CanaryWithStats) => (
        <span className={c.alertCount24h > 0 ? 'text-danger-600 font-medium' : 'text-gray-600'}>
          {c.alertCount24h}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (c: CanaryWithStats) => <StatusBadge isActive={c.isActive} />,
    },
    {
      key: 'lastDetection',
      header: 'Last Detection',
      render: (c: CanaryWithStats) =>
        formatListTimestamp(c.lastDetectionAt),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Canaries</h1>
        <div className="flex items-center gap-4">
          {organizations && organizations.length > 1 && (
            <select
              value={orgId ?? ''}
              onChange={(e) => { setSelectedOrgId(e.target.value); }}
              className="input max-w-xs"
            >
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          )}
          <Button onClick={() => { navigate(`/canaries/new${orgId ? `?orgId=${orgId}` : ''}`); }}>
            Create Canary
          </Button>
        </div>
      </div>

      {error ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-danger-600">Failed to load canaries</p>
          </div>
        </Card>
      ) : (
        <Card>
          <Table
            columns={columns}
            data={canaries ?? []}
            keyExtractor={(c) => c.id}
            onRowClick={(c) => { navigate(`/canaries/${c.id}`); }}
            isLoading={isLoading}
            emptyMessage="No canaries yet. Create your first canary to start protecting your pages."
          />
        </Card>
      )}
    </div>
  )
}

